// Período da DRE → unidades que o backend sabe calcular (trimestres e meses).
//
// Fica aqui, fora do Dashboard.tsx, por dois motivos: é lógica de calendário
// com bordas perigosas (janeiro, virada de trimestre, dezembro, ano futuro), e
// assim dá pra testar sem importar antd — o jest do CRA5 não carrega o antd v6
// e a suíte inteira falharia ao montar.

export const isAnual = (p: string) => /^\d{4}$/.test(p);
export const isSemestral = (p: string) => p.includes('/S');
export const isMensal = (p: string) => /^\d{4}\/\d{2}$/.test(p);

// A agregação por soma de trimestres é a correta inclusive pros impostos:
// IRPJ/CSLL do Lucro Presumido apuram por trimestre — semestre/ano = soma.
//
// ⚠ ANO EM CURSO CORTA NO MÊS ATUAL (14/08/2026). Antes, "Anual" somava os 4
//   trimestres CHEIOS — e mês futuro entra só com o lado da DESPESA, porque
//   despesa é lançada assim que o compromisso existe (parcelas de honorários,
//   DARF provisionado, provisão de alimentação) enquanto receita só conta
//   quando fatura.
//   Medido em produção: o Q4/2026 entrava com receita ZERO e despesa de
//   R$ 607.774, e a tela mostrava PREJUÍZO de R$ 53.174 num ano que tinha
//   LUCRO de R$ 760.151. A receita desses meses existe (R$ 1.589.958 previstos
//   de set a dez), só não é reconhecida ainda.
//   Por isso o ano corrente vira: trimestres já COMPLETOS + os meses do
//   trimestre em curso até hoje. Ano passado segue com os 4 trimestres.
export function trimestresDe(p: string, hoje: Date = new Date()): string[] {
  if (isSemestral(p)) {
    const [ano, s] = p.split('/');
    return s === 'S1' ? [`${ano}/Q1`, `${ano}/Q2`] : [`${ano}/Q3`, `${ano}/Q4`];
  }
  if (isAnual(p)) {
    const ano = parseInt(p, 10);
    const anoAtual = hoje.getFullYear();
    if (ano < anoAtual) return ['Q1', 'Q2', 'Q3', 'Q4'].map(q => `${p}/${q}`);
    if (ano > anoAtual) return [];                      // ano futuro: nada a somar
    const mesAtual = hoje.getMonth() + 1;               // 1..12
    const trisCompletos = Math.floor((mesAtual - 1) / 3);
    const unidades: string[] = [];
    for (let q = 1; q <= trisCompletos; q++) unidades.push(`${p}/Q${q}`);
    // Meses soltos do trimestre em curso — pegar o trimestre inteiro traria
    // mês que ainda não aconteceu.
    for (let m = trisCompletos * 3 + 1; m <= mesAtual; m++) {
      unidades.push(`${p}/${String(m).padStart(2, '0')}`);
    }
    return unidades;
  }
  return [p]; // trimestre único ou mês único
}

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun',
                   'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Rótulo do período realmente coberto — para ninguém achar que "Anual" de um
// ano em curso já é o ano fechado. Devolve null quando não se aplica.
export function rotuloCobertura(p: string, hoje: Date = new Date()): string | null {
  if (!isAnual(p)) return null;
  if (parseInt(p, 10) !== hoje.getFullYear()) return null;
  return `jan–${MES_CURTO[hoje.getMonth()]} · ano em curso (meses futuros ficam de fora)`;
}

// Peso de cada TRIMESTRE dentro da seleção — usado no "vs Orçamento", que é
// cadastrado por trimestre e não tem quebra mensal. Sem o peso, o trimestre em
// curso compararia 2 meses realizados contra 3 meses orçados e toda linha
// pareceria abaixo do orçado.
export function pesoTrimestres(unidades: string[]): Record<string, number> {
  const peso: Record<string, number> = {};
  for (const u of unidades) {
    if (u.includes('Q')) { peso[u] = (peso[u] || 0) + 1; continue; }
    const [ano, mm] = u.split('/');
    const tri = `${ano}/Q${Math.ceil(parseInt(mm, 10) / 3)}`;
    peso[tri] = (peso[tri] || 0) + 1 / 3;
  }
  return peso;
}

// ─── Competência do LANÇAMENTO MANUAL ────────────────────────────────────────
//
// 🚨 O único formato que o motor da DRE enxerga é 'AAAA/MM'. O motor casa por
// IGUALDADE: mês solto usa `lm.periodo = $1`, trimestre usa `lm.periodo =
// ANY($1)` com ['AAAA/01','AAAA/02','AAAA/03'] (dre-engine/index.js). Já a
// LISTAGEM da tela casa por `periodo LIKE 'AAAA/%'` (handlers/manuais.js) —
// ou seja, '2025/1' aparece bonito na lista e NUNCA entra em nenhuma DRE.
// Com o estorno de competência isso deixou de ser detalhe: a pessoa digita
// um período de OUTRO ano, à mão, e o lançamento inteiro é um silêncio de
// R$ 496.100 se o mês vier sem o zero.
export function ehCompetenciaManual(p: string): boolean {
  return /^\d{4}\/(0[1-9]|1[0-2])$/.test((p || '').trim());
}

// Conserta o que é claramente a mesma competência escrita torto ('2025/1',
// '2025-12', ' 2025 / 03 ') e devolve o resto como veio — quem recusa é o
// `ehCompetenciaManual`, não isto aqui.
export function normalizarCompetencia(p: string): string {
  const m = (p || '').trim().match(/^(\d{4})\s*[/\-.]\s*(\d{1,2})$/);
  if (!m) return (p || '').trim();
  const mes = parseInt(m[2], 10);
  if (mes < 1 || mes > 12) return (p || '').trim();
  return `${m[1]}/${String(mes).padStart(2, '0')}`;
}
